import { useRef, useState } from "react";
import { api } from "@/lib/api";
import { PROGRAM_TYPES } from "./constants";

export default function ProgramEditor({ brandId, value, onChange, onSave, onCancel }) {
  const set = (patch) => onChange({ ...value, ...patch });
  const fileRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [uploadErr, setUploadErr] = useState("");

  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true); setUploadErr("");
    try {
      const form = new FormData();
      form.append("file", file);
      const { data } = await api.post(`/files/upload?brand_id=${brandId}`, form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      set({ creative_sample: { pdf_url: data.url, filename: file.name, inspection_status: "pending" } });
    } catch (err) {
      setUploadErr(err.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  return (
    <div data-testid="brand-programs-editor" className="border border-[#1A1F2C]/20 bg-white rounded-lg p-6 mb-6">
      <h3 className="font-serif text-lg mb-4">{value.program_id ? "Edit program" : "New program"}</h3>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <label className="block text-[10px] uppercase tracking-[0.18em] text-[#6B6358] mb-1">Program name</label>
          <input
            data-testid="brand-programs-editor-name"
            value={value.name}
            onChange={(e) => set({ name: e.target.value })}
            placeholder="Summer Flyer Wrap"
            className="w-full px-3 py-2 border border-[#1A1F2C]/20 rounded-md bg-[#F8F5EE] text-sm"
          />
        </div>
        <div>
          <label className="block text-[10px] uppercase tracking-[0.18em] text-[#6B6358] mb-1">Program type</label>
          <select
            data-testid="brand-programs-editor-type"
            value={value.program_type}
            onChange={(e) => set({ program_type: e.target.value })}
            className="w-full px-3 py-2 border border-[#1A1F2C]/20 rounded-md bg-[#F8F5EE] text-sm"
          >
            {PROGRAM_TYPES.map((t) => (
              <option key={t.id} value={t.id} disabled={!t.enabled}>
                {t.label}{t.enabled ? "" : " (coming soon)"}
              </option>
            ))}
          </select>
        </div>
      </div>

      <div className="mt-4">
        <label className="block text-[10px] uppercase tracking-[0.18em] text-[#6B6358] mb-1">
          Creative sample (PDF, required)
        </label>
        {value.creative_sample?.pdf_url ? (
          <div className="flex items-center gap-3 text-sm">
            <a href={value.creative_sample.pdf_url} target="_blank" rel="noreferrer" className="text-[#1F6B6B] underline">
              {value.creative_sample.filename || "View"}
            </a>
            <button
              type="button"
              onClick={() => set({ creative_sample: null })}
              className="text-xs text-[#B5462F]"
            >
              Remove
            </button>
          </div>
        ) : (
          <input
            ref={fileRef}
            type="file"
            accept="application/pdf"
            data-testid="brand-programs-editor-upload"
            onChange={upload}
            disabled={uploading}
            className="text-sm"
          />
        )}
        {uploading && <p className="text-xs text-[#6B6358] mt-1">Uploading…</p>}
        {uploadErr && <p className="text-xs text-[#B5462F] mt-1">{uploadErr}</p>}
      </div>

      <div className="mt-5 flex gap-2 justify-end">
        <button onClick={onCancel} className="px-4 py-2 text-xs uppercase tracking-[0.18em] text-[#6B6358]">Cancel</button>
        <button
          data-testid="brand-programs-editor-save"
          onClick={onSave}
          disabled={!value.name || !value.creative_sample?.pdf_url}
          title={!value.creative_sample?.pdf_url ? "A creative sample is required" : ""}
          className="px-5 py-2 bg-[#1A1F2C] text-[#F8F5EE] rounded-full text-xs uppercase tracking-[0.18em] disabled:opacity-50"
        >
          Save program
        </button>
      </div>
    </div>
  );
}
